import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import {
  CAMPAIGN_FILTERS,
  CAMPAIGN_FILTERS_WITHOUT_FORM_TRIGGERED,
  CAMPAIGN_STATE_FILTERS,
} from 'cio/utils/campaign-newsletter-filters';
import CampaignModel, { CampaignState, CampaignType } from 'cio/models/campaign';
import TagModel from 'cio/models/tag';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import FeaturesService from 'ember-feature-flags/services/features';
import { task } from 'ember-concurrency';
import { isPresent } from '@ember/utils';
import DS from 'ember-data';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';
import ArrayProxy from '@ember/array/proxy';
import SettingsService from 'cio/services/settings';
import moment from 'moment-timezone';
import { Grouping, Measure } from 'cio/utils/metrics/common';
import { SortDirection } from 'cio/utils/sort';
import { CAMPAIGN_METRIC_SORT_OPTIONS, CAMPAIGN_SORT_OPTIONS } from 'cio/utils/campaigns/sort-options';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { getDateRangePresetByValue, resolutionByRange } from 'cio/utils/campaigns/date-range';
import { LAST_30_DAYS_RANGE_PRESET } from 'cio/utils/workflow-metrics';
import RouterService from '@ember/routing/router-service';
import MomentService from 'ember-moment/services/moment';
import ExperimentsService from 'cio/services/experiments';

export default class IndexController extends Controller {
  @service auth!: AuthService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service settings!: SettingsService;
  @service router!: RouterService;
  @service moment!: MomentService;
  @service experiments!: ExperimentsService;

  declare model: CampaignModel;

  sortOptions = CAMPAIGN_SORT_OPTIONS;
  measureDropdownItems = CAMPAIGN_METRIC_SORT_OPTIONS;

  queryParams = [
    'type',
    'searchType', // same as "type" but multi-value and not accessible by UI controls
    'sendToUnsubscribed',
    'page',
    'searchString',
    'searchTagIds',
    'sort',
    'asc',
    'archived',
    'state',
    'subscriptionTopicId',
    'objectTypeIds',
  ];
  @tracked type = 'null';
  @tracked searchType: string[] = [];
  @tracked sendToUnsubscribed: boolean | null = null;
  @tracked page = 1;
  @tracked searchString = '';
  @tracked searchTagIds: string[] = [];
  @tracked sort = '';
  @tracked sortUI = ''; // same as "sort" but accessible by UI controls
  @tracked asc = '';
  @tracked ascUI = false; // same as "asc" but accessible by UI controls
  @tracked archived = false;
  @tracked state: keyof typeof CAMPAIGN_STATE_FILTERS | null = null;
  @tracked subscriptionTopicId: string | null = null;
  @tracked objectTypeIds: string[] = [];
  @tracked measures: string[] = [];
  @tracked resolution: Grouping | null = null;
  @tracked range: DateRange = LAST_30_DAYS_RANGE_PRESET.getRange();
  @tracked createCampaignRoute = this.getRoute();

  itemsPerPage = 20;

  getCampaignTemplateGalleryVariation() {
    // Only add a user to this experiment if they are a new user
    let createdAt = this.auth.user?.createdAt ?? 0;
    let EXPERIMENT_START = this.moment.moment('2024-03-05').unix();
    if (EXPERIMENT_START <= createdAt) {
      return this.experiments.getExperimentVariation('campaignTemplateGalleryExperiment');
    }

    // Don't include other users in the experiment at all
    return null;
  }

  getRoute() {
    let variation = this.getCampaignTemplateGalleryVariation();
    return variation === 'campaign_template_gallery_variation_a' ? 'campaigns.welcome.create' : 'campaigns.new';
  }

  fetchCampaigns = task({ restartable: true }, async () => {
    let {
      type,
      searchType,
      sendToUnsubscribed,
      page,
      searchString,
      searchTagIds,
      sort,
      asc,
      archived,
      state,
      subscriptionTopicId,
      objectTypeIds,
      resolution,
      metricsTz,
    } = this;

    this.sortUI = sort !== '' ? sort : this.sortUI;
    this.ascUI = asc !== '' ? asc === 'true' : this.ascUI;

    try {
      let results = await this.store.query('campaign', {
        type,
        searchType,
        sendToUnsubscribed,
        page: Number(page) - 1,
        searchString,
        searchTagIds,
        asc: this.ascUI,
        archived,
        state,
        subscriptionTopicId,
        objectTypeIds,
        sort: this.sortUI,
        start: this.metricTzRange.start.unix(),
        end: this.metricTzRange.end.unix(),
        version: 2,
        res: resolution,
        tz: metricsTz,
      });

      let tags: DS.RecordArray<TagModel> | ArrayProxy<TagModel> = this.store.peekAll('tag');

      if (tags.toArray().length === 0) {
        tags = await this.store.findAll('tag');
      }

      this.analytics.track('campaign', 'list_viewed', {
        model: this.model,
        tags,
      });

      this.hasFetchedCampaignsAtLeastOnce = true;

      return results;
    } catch (e) {
      this.apiErrors.report(e);
    }

    return [] as CampaignModel[];
  });

  fetchUserSettings = task({ restartable: true }, async () => {
    let result = (await this.auth.user?.getUserSettings()) ?? {};

    if (result.campaigns_page == null) {
      return;
    }

    let { is_columns_updated, columns, sort, preset_range } = result.campaigns_page;
    let { metric, asc } = this.parseSortSettings(sort);
    let range = getDateRangePresetByValue(preset_range).getRange();

    this.range = range;
    this.resolution = resolutionByRange(range);
    this.sortUI = metric;
    this.ascUI = asc;
    this.measures = is_columns_updated ? columns : [...this.defaultMeasures];
  });

  updateCampaignsPageSettings = task({ restartable: true }, async (value: Record<string, any>) => {
    await this.auth.user?.updateUserSettings({
      campaigns_page: value,
    });
  });

  @tracked hasFetchedCampaignsAtLeastOnce = false;

  @readOnly('fetchCampaigns.lastSuccessful.value.meta.pagination.total') total!: number;
  @readOnly('fetchCampaigns.lastSuccessful.value.meta.totals.campaigns') campaignCount!: number;
  @readOnly('fetchCampaigns.lastSuccessful.value.meta.totals.archived') archivedCount!: number;
  @readOnly('fetchCampaigns.lastSuccessful.value.meta.totals.has_campaigns') hasCampaigns!: boolean;

  get allCampaigns() {
    return this.fetchCampaigns.lastSuccessful?.value;
  }

  get metricTzRange() {
    if (this.resolution === Grouping.Hourly) {
      // this is in case user selects Last 24 hours preset
      return {
        start: moment.tz(this.range?.start, this.metricsTz),
        end: moment.tz(this.range?.end, this.metricsTz),
      };
    }
    return {
      start: moment.tz(`${this.range?.start.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${this.range?.end.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };
  }

  get metricsTz() {
    return this.settings.environmentSettings.metricsTimezone;
  }

  get results() {
    return this.total > 0;
  }

  get defaultSortSetting() {
    return {
      metric: this.environment.current.metricDenominator ?? 'sent',
      asc: false,
    };
  }

  get shouldDisplayCampaignsWelcome() {
    return this.hasCampaigns === false && this.archivedCount <= 0;
  }

  @computed('environment.current.canIdentifyByEmail')
  get filters() {
    let f = this.environment.current.canIdentifyByEmail ? CAMPAIGN_FILTERS : CAMPAIGN_FILTERS_WITHOUT_FORM_TRIGGERED;

    return f.filter((filter) => {
      if (this.features.isEnabled('campaign_trigger_ui_new')) {
        return true;
      }

      return filter.value !== CampaignType.ObjectRelationship;
    });
  }

  @computed('environment.current.metricDenominator')
  get defaultMeasures() {
    return [this.environment.current.metricDenominator ?? 'sent', 'opened', 'clicked', 'converted'];
  }

  get sortDirection(): SortDirection {
    return this.ascUI ? SortDirection.Ascending : SortDirection.Descending;
  }

  get isCampaignsTableLoading() {
    return this.fetchCampaigns.isRunning && this.hasFetchedCampaignsAtLeastOnce;
  }

  get isPageLoading() {
    return (this.fetchCampaigns.isRunning || this.fetchUserSettings.isRunning) && !this.hasFetchedCampaignsAtLeastOnce;
  }

  @action
  setDefaults() {
    this.type = 'null';
    this.searchType = [];
    this.sendToUnsubscribed = null;
    this.page = 1;
    this.searchString = '';
    this.sort = '';
    this.sortUI = '';
    this.asc = '';
    this.ascUI = false;
    this.searchTagIds = [];
    this.archived = false;
    this.state = null;
    this.subscriptionTopicId = null;
    this.objectTypeIds = [];
  }

  @action
  resetPage() {
    this.page = 1;
  }

  @action
  resetPageIfNecessary() {
    // After a campaign gets archived or deleted, reset pagination if the
    // campaign was the last one on that page (so that we don't show an empty
    // page)
    let modelCount = this.total;
    let maxPage = Math.max(Math.ceil(modelCount / this.itemsPerPage), 1);
    let hasExceededMaxPage = this.page > maxPage;

    if (hasExceededMaxPage) {
      this.page = maxPage;
    }
  }

  @action
  onRangeSelect(range: DateRange, resolution: Grouping, preset?: string) {
    this.range = range;
    this.resolution = resolution;
    if (preset != null) {
      this.updateCampaignsPageSettings.perform({
        preset_range: preset,
      });
    }

    this.refreshCampaigns();
  }

  @action
  addTagFilter(tagIDs: string[]) {
    this.searchTagIds = tagIDs;
  }

  @action
  onTagClicked(tag: TagModel) {
    this.searchString = '';
    this.searchTagIds = [tag.get('id')];
    this.type = 'null';
  }

  @action
  onObjectTypeIdsChange(objectIds: string[]) {
    this.objectTypeIds = objectIds;
  }

  @action
  async refreshCampaigns() {
    await this.fetchCampaigns.perform();

    if (this.shouldDisplayCampaignsWelcome) {
      this.router.transitionTo('campaigns.welcome');
      return;
    }
  }

  @action
  toggleArchived(archived: boolean) {
    if (this.archived === archived) {
      return;
    }

    this.resetPage();
    this.archived = archived;
  }

  @action
  onCampaignStateChange(
    item: {
      label: string;
      value: CampaignState;
    } | null,
  ) {
    if (item != null) {
      this.page = 1;
      this.state = item.value;
    } else {
      this.page = 1;
      this.state = null;
    }
  }

  @action
  onSearch(searchString: string) {
    this.page = 1;
    this.searchString = searchString;
  }

  @action
  onClearSearch() {
    if (isPresent(this.searchString)) {
      this.page = 1;
      this.searchString = '';
    }
  }

  @action
  onMeasuresChange(measures: string[]) {
    this.measures = [...measures].sort(
      (a, b) =>
        this.measureDropdownItems.findIndex((option) => a === option.value) -
        this.measureDropdownItems.findIndex((option) => b === option.value),
    );

    this.updateCampaignsPageSettings.perform({
      columns: this.measures,
    });
  }

  @action
  onSetSortDescending(sortDescending: boolean) {
    this.ascUI = !sortDescending;
    this.asc = String(this.ascUI);
    this.updateCampaignsPageSettings.perform({
      sort: this.serializeSortSettings(this.sortUI, this.ascUI),
    });
  }

  @action
  onChangeSubscriptionTopic(
    topic: {
      label: string;
      value: string;
    } | null,
  ) {
    this.subscriptionTopicId = topic?.value ?? null;
  }

  @action
  onSortDropdownChange(sort: string) {
    this.sort = sort;
    this.sortUI = sort;
    this.updateCampaignsPageSettings.perform({
      sort: this.serializeSortSettings(sort, this.ascUI),
    });
  }

  @action
  onSortColumnHeader(sort: Measure, sortDirection: SortDirection) {
    this.sortUI = sort;
    this.sort = sort;
    this.ascUI = sortDirection === SortDirection.Ascending;
    this.asc = String(this.ascUI);
    this.updateCampaignsPageSettings.perform({
      sort: this.serializeSortSettings(sort, this.ascUI),
    });
  }

  private parseSortSettings(sort: string) {
    // "metric:asc|desc"
    let [metric, dir] = sort.split(':');

    // Check if the sort string is valid
    if (!metric || (dir !== SortDirection.Ascending && dir !== SortDirection.Descending)) {
      return this.defaultSortSetting;
    }

    return {
      metric,
      asc: dir === SortDirection.Ascending,
    };
  }

  private serializeSortSettings(metric: string, asc: boolean) {
    return `${metric}:${asc ? 'asc' : 'desc'}`;
  }
}
